import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, ArrowLeft, PawPrint, Leaf, Package, Dog, Cat } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';

// Struttura categorie ad albero
const CATEGORY_TREE = {
  petshop: {
    label: 'Pet Shop',
    icon: PawPrint,
    color: 'bg-rose-500',
    hoverColor: 'hover:bg-rose-600',
    lightColor: 'bg-rose-50',
    textColor: 'text-rose-600',
    description: 'Tutto per i tuoi amici a quattro zampe',
    image: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&h=300&fit=crop',
    subcategories: {
      cane: {
        label: 'Cane',
        icon: Dog,
        description: 'Prodotti per cani',
        image: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&h=300&fit=crop',
        subcategories: [
          { value: 'cane_cibo_umido', label: 'Cibo Umido', emoji: '🥫' },
          { value: 'cane_cibo_secco', label: 'Cibo Secco', emoji: '🦴' }
        ]
      },
      gatto: {
        label: 'Gatto',
        icon: Cat,
        description: 'Prodotti per gatti',
        image: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=400&h=300&fit=crop',
        subcategories: [
          { value: 'gatto_cibo_secco', label: 'Cibo Secco', emoji: '🐟' },
          { value: 'gatto_cibo_umido', label: 'Cibo Umido', emoji: '🥫' }
        ]
      }
    }
  },
  giardinaggio: {
    label: 'Giardinaggio',
    icon: Leaf,
    color: 'bg-emerald-500',
    hoverColor: 'hover:bg-emerald-600',
    lightColor: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    description: 'Piantine, semi e attrezzi per il tuo orto',
    image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop',
    subcategories: {}
  },
  varie: {
    label: 'Varie',
    icon: Package,
    color: 'bg-amber-500',
    hoverColor: 'hover:bg-amber-600',
    lightColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    description: 'Altri prodotti utili per te',
    image: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=400&h=300&fit=crop',
    subcategories: {}
  }
};

export default function CategoriesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialLevel = searchParams.get('level') || 'main';
  
  const [currentLevel, setCurrentLevel] = useState(initialLevel);
  const [breadcrumb, setBreadcrumb] = useState([]);

  // Imposta breadcrumb iniziale basato sul parametro URL
  useEffect(() => {
    if (initialLevel === 'petshop') {
      setBreadcrumb([{ key: 'petshop', label: 'Pet Shop' }]);
      setCurrentLevel('petshop');
    } else if (initialLevel === 'petshop_cane') {
      setBreadcrumb([
        { key: 'petshop', label: 'Pet Shop' },
        { key: 'petshop_cane', label: 'Cane' }
      ]);
      setCurrentLevel('petshop_cane');
    } else if (initialLevel === 'petshop_gatto') {
      setBreadcrumb([
        { key: 'petshop', label: 'Pet Shop' },
        { key: 'petshop_gatto', label: 'Gatto' }
      ]);
      setCurrentLevel('petshop_gatto');
    }
  }, [initialLevel]);

  const goToProducts = (category, subcategory = null) => {
    const params = new URLSearchParams();
    params.set('category', category);
    if (subcategory) params.set('subcategory', subcategory);
    navigate(`/products?${params.toString()}`);
  };

  const navigateToLevel = (level, crumb) => {
    setCurrentLevel(level);
    setBreadcrumb(crumb);
  };

  const goBack = () => {
    if (breadcrumb.length > 0) {
      const newBreadcrumb = [...breadcrumb];
      newBreadcrumb.pop();
      
      if (newBreadcrumb.length === 0) {
        setCurrentLevel('main');
      } else if (newBreadcrumb.length === 1) {
        setCurrentLevel(newBreadcrumb[0].key);
      }
      setBreadcrumb(newBreadcrumb);
    }
  };

  // Render delle categorie principali
  const renderMainCategories = () => (
    <div className="grid md:grid-cols-3 gap-6">
      {Object.entries(CATEGORY_TREE).map(([key, cat]) => {
        const Icon = cat.icon;
        const hasSubcategories = Object.keys(cat.subcategories).length > 0;
        
        return (
          <button
            key={key}
            onClick={() => {
              if (hasSubcategories) {
                navigateToLevel(key, [{ key, label: cat.label }]);
              } else {
                goToProducts(key);
              }
            }}
            className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
          >
            {/* Immagine di sfondo */}
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={cat.image}
                alt={cat.label}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            </div>
            
            {/* Contenuto */}
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className={`inline-flex items-center justify-center w-12 h-12 ${cat.color} rounded-full mb-3`}>
                <Icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-1">{cat.label}</h3>
              <p className="text-white/80 text-sm mb-3">{cat.description}</p>
              
              <div className="flex items-center text-white/90 text-sm font-medium">
                {hasSubcategories ? (
                  <>
                    <span>Esplora sottocategorie</span>
                    <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </>
                ) : (
                  <>
                    <span>Vedi prodotti</span>
                    <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );

  // Render sottocategorie Pet Shop (Cane/Gatto)
  const renderPetshopSubcategories = () => {
    const petshop = CATEGORY_TREE.petshop;
    
    return (
      <div className="space-y-6">
        {/* Opzione per vedere tutti i prodotti Pet Shop */}
        <button
          onClick={() => goToProducts('petshop')}
          className="w-full p-4 bg-rose-50 border-2 border-rose-200 rounded-xl hover:bg-rose-100 transition-colors flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <PawPrint className="h-6 w-6 text-rose-600" />
            <span className="font-semibold text-rose-700">Vedi tutti i prodotti Pet Shop</span>
          </div>
          <ChevronRight className="h-5 w-5 text-rose-600 group-hover:translate-x-1 transition-transform" />
        </button>

        <div className="grid md:grid-cols-2 gap-6">
          {Object.entries(petshop.subcategories).map(([key, sub]) => {
            const Icon = sub.icon;
            
            return (
              <button
                key={key}
                onClick={() => navigateToLevel(`petshop_${key}`, [...breadcrumb, { key: `petshop_${key}`, label: sub.label }])}
                className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={sub.image}
                    alt={sub.label}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                </div>
                
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-rose-500 rounded-full mb-3">
                    {key === 'cane' ? '🐕' : '🐈'}
                  </div>
                  <h3 className="text-2xl font-bold mb-1">{sub.label}</h3>
                  <p className="text-white/80 text-sm mb-3">{sub.description}</p>
                  
                  <div className="flex items-center text-white/90 text-sm font-medium">
                    <span>Scegli tipo di cibo</span>
                    <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Render sottocategorie finali (Cibo Umido/Secco)
  const renderFinalSubcategories = (animalKey) => {
    const animal = CATEGORY_TREE.petshop.subcategories[animalKey];
    
    return (
      <div className="space-y-6">
        {/* Opzione per vedere tutti i prodotti dell'animale */}
        <button
          onClick={() => goToProducts('petshop', animalKey)}
          className="w-full p-4 bg-rose-50 border-2 border-rose-200 rounded-xl hover:bg-rose-100 transition-colors flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{animalKey === 'cane' ? '🐕' : '🐈'}</span>
            <span className="font-semibold text-rose-700">Vedi tutti i prodotti per {animal.label}</span>
          </div>
          <ChevronRight className="h-5 w-5 text-rose-600 group-hover:translate-x-1 transition-transform" />
        </button>

        <div className="grid md:grid-cols-2 gap-6">
          {animal.subcategories.map((sub) => (
            <button
              key={sub.value}
              onClick={() => goToProducts('petshop', sub.value)}
              className="group p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-rose-300"
            >
              <div className="text-6xl mb-4">{sub.emoji}</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{sub.label}</h3>
              <p className="text-gray-600 text-sm mb-4">
                {sub.label} per {animal.label.toLowerCase()}
              </p>
              
              <div className="flex items-center text-rose-600 text-sm font-medium">
                <span>Vedi prodotti</span>
                <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Determina cosa renderizzare in base al livello corrente
  const renderContent = () => {
    if (currentLevel === 'main') {
      return renderMainCategories();
    } else if (currentLevel === 'petshop') {
      return renderPetshopSubcategories();
    } else if (currentLevel === 'petshop_cane') {
      return renderFinalSubcategories('cane');
    } else if (currentLevel === 'petshop_gatto') {
      return renderFinalSubcategories('gatto');
    }
    return renderMainCategories();
  };

  // Titolo dinamico
  const getTitle = () => {
    if (currentLevel === 'main') return 'Scegli una Categoria';
    if (currentLevel === 'petshop') return 'Pet Shop';
    if (currentLevel === 'petshop_cane') return 'Prodotti per Cane';
    if (currentLevel === 'petshop_gatto') return 'Prodotti per Gatto';
    return 'Categorie';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-6xl py-8">
        {/* Header con breadcrumb */}
        <div className="mb-8">
          {breadcrumb.length > 0 && (
            <Button
              variant="ghost"
              onClick={goBack}
              className="mb-4 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Indietro
            </Button>
          )}

          {/* Breadcrumb */}
          {breadcrumb.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
              <button 
                onClick={() => { setCurrentLevel('main'); setBreadcrumb([]); }}
                className="hover:text-rose-600 transition-colors"
              >
                Categorie
              </button>
              {breadcrumb.map((item, index) => (
                <span key={item.key} className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4" />
                  <button
                    onClick={() => {
                      const newBreadcrumb = breadcrumb.slice(0, index + 1);
                      setBreadcrumb(newBreadcrumb);
                      setCurrentLevel(item.key);
                    }}
                    className={index === breadcrumb.length - 1 ? 'text-rose-600 font-medium' : 'hover:text-rose-600 transition-colors'}
                  >
                    {item.label}
                  </button>
                </span>
              ))}
            </div>
          )}

          <h1 className="text-4xl font-extrabold text-gray-800">{getTitle()}</h1>
          {currentLevel === 'main' && (
            <p className="text-gray-600 mt-2">Seleziona una categoria per esplorare i nostri prodotti</p>
          )}
        </div>

        {/* Contenuto categorie */}
        {renderContent()}

        {/* Link diretto a tutti i prodotti */}
        <div className="mt-12 text-center">
          <Button
            variant="outline"
            onClick={() => navigate('/products')}
            className="text-gray-600 hover:text-gray-800"
          >
            Oppure vedi tutti i prodotti →
          </Button>
        </div>
      </div>
    </div>
  );
}
